import { FilterOptionDto } from '@act/shared/data-transfer-objects';
import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  TextField,
} from '@mui/material';
import {
  CSSProperties,
  Fragment,
  HTMLAttributes,
  SyntheticEvent,
  useMemo,
} from 'react';
import { Path, useController } from 'react-hook-form';
import { BaseControlledInputProps } from '../basicControlledInputProps.types';
import FormFieldWrappers from '../FormFieldWrappers';
import FormErrorHelper from '../SharedComponents/FormErrorHelper';
import { parseError } from '../form-utils/errorParser.util';
import {
  useFormTracking,
  useFormOptionTracking,
  useStableSelectedOptions,
} from '../hooks';
import type { AutocompleteSelection } from '../hooks';

type AutocompleteProps = {
  /**
   * The options to display in the autocomplete
   */
  options: FilterOptionDto[];
  /**
   * A start adornment for the autocomplete; an icon or React node
   */
  startAdornment?: JSX.Element;
  /**
   * Whether the autocomplete supports multiple selections
   */
  multiple?: boolean;
  /**
   * The maximum number of tags to display, -1 for no limit
   */
  limitTags?: number;
  isOptionEqualToValue?: (
    option: FilterOptionDto,
    value: FilterOptionDto
  ) => boolean;
  /**
   * Defines how an option label is gotten and displayed
   *
   * @param TFieldValue - the option object with the label in it
   * @returns the label for the option
   */
  getOptionLabel: (TFieldValue) => string;
  /**
   * Style properties
   */
  style?: CSSProperties;
  groupBy?: (FilterOptionDto) => string;
  /**
   * Placeholder text for the input field
   */
  placeholder?: string;
  /**
   * Callback fired when the input value changes
   */
  onInputChange?: (
    event: SyntheticEvent,
    value: string,
    reason: string
  ) => void;
  /**
   * Disable built-in filtering (useful when doing server-side filtering)
   */
  disableFiltering?: boolean;
  /**
   * Whether the autocomplete is loading options
   */
  loading?: boolean;
};

export type ControlledAutocompleteProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>,
> = BaseControlledInputProps<FORM_TYPE, FIELD_NAME> & AutocompleteProps;

/**
 * Renders a react-hook-form controlled MUI autocomplete over `FilterOptionDto`
 * options, storing the primitive option value(s) in the form.
 *
 * @param props - Field wiring, option list, and autocomplete display options.
 * @returns The controlled autocomplete field.
 */
export const ControlledAutocomplete = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>,
>({
  name,
  label,
  control,
  options,
  disabled = false,
  multiple = false,
  groupBy,
  isOptionEqualToValue = (option, value) => {
    return option?.value === value?.value;
  },
  limitTags = -1,
  errors = undefined,
  startAdornment = undefined,
  style,
  getOptionLabel,
  trackingName,
  sensitive = true,
  placeholder,
  onInputChange,
  disableFiltering = false,
  loading = false,
}: ControlledAutocompleteProps<FORM_TYPE, FIELD_NAME>) => {
  // Auto-generate tracking props if trackingName is provided
  const trackingProps = useFormTracking(trackingName, sensitive);
  const { generateOptionTracking } = useFormOptionTracking(
    trackingName,
    sensitive,
    options
  );

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name, control });

  // Primitive key so a form value that is re-created with identical contents
  // does not produce a new selection reference.
  const selectionKey = multiple
    ? (Array.isArray(value) ? value : []).join('\u0000')
    : String(value ?? '');

  const derivedSelection = useMemo(() => {
    if (multiple) {
      const selectedValues = Array.isArray(value) ? value : [];
      return options.filter(option => selectedValues.includes(option.value));
    }

    return options.find(option => option.value === value) ?? null;
    // `selectionKey` stands in for `value`, which may be a new array each render.
  }, [multiple, options, selectionKey]);

  const selectedValue = useStableSelectedOptions(derivedSelection);

  const handleChange = (_, data: AutocompleteSelection) => {
    multiple
      ? onChange((data as FilterOptionDto[]).map(item => item.value))
      : onChange((data as FilterOptionDto)?.value);
  };

  return (
    <FormFieldWrappers.FieldWrapper>
      <FormFieldWrappers.InputWrapper>
        <Autocomplete<FilterOptionDto, boolean, false, false>
          {...trackingProps}
          style={style}
          value={selectedValue}
          options={options}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          onChange={handleChange}
          onInputChange={onInputChange}
          filterOptions={disableFiltering ? x => x : undefined}
          multiple={multiple}
          limitTags={limitTags}
          groupBy={groupBy}
          disabled={disabled}
          loading={loading}
          renderOption={(props, option) => {
            const { key, ...optionProps } =
              props as HTMLAttributes<HTMLLIElement> & {
                key?: string;
              };

            return (
              <li
                key={key}
                {...optionProps}
                {...generateOptionTracking(option.value, option.label)}
              >
                {option.label}
              </li>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              placeholder={placeholder}
              error={!!error}
              helperText={error?.message}
              sx={{
                backgroundColor: 'white',
                borderRadius: '16px',
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <Fragment>
                    {startAdornment ? (
                      <InputAdornment
                        position="start"
                        sx={{ marginLeft: '4px' }}
                      >
                        {startAdornment}
                        {params.InputProps.startAdornment}
                      </InputAdornment>
                    ) : (
                      <Fragment>{params.InputProps.startAdornment}</Fragment>
                    )}
                  </Fragment>
                ),
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          )}
        />
      </FormFieldWrappers.InputWrapper>
      {errors && parseError(name, errors) && (
        <FormErrorHelper name={name} errors={errors} />
      )}
    </FormFieldWrappers.FieldWrapper>
  );
};
